import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, X, Palette, ChevronDown, Bell, KeyRound, Loader2, UserCog, Moon, Sun, Search, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme, THEMES } from "../context/ThemeContext";
import { NAV, GROUP_ORDER } from "../lib/nav";
import { smartSearch } from "../lib/search";
import api, { formatApiError } from "../lib/api";
import GlobalSearch from "./GlobalSearch";
import PushToggle from "./PushToggle";
import { GtClock } from "./GtClock";
import WelcomeTour from "./WelcomeTour";
import ScreenHelp from "./ScreenHelp";
import { slugForRoute } from "../lib/helpData";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 min-h-[4rem] border-b border-white/10" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <img src="/logo-selec.jpg" alt="SELEC" className="h-10 w-10 rounded-xl object-cover shrink-0 ring-1 ring-white/20" />
      <div className="leading-none">
        <p className="font-heading font-black text-lg text-sidebar-foreground tracking-tight">SELEC <span className="text-primary">ONE 360</span></p>
        <p className="text-[9px] uppercase tracking-[0.22em] text-sidebar-foreground/50 font-bold">La fuerza detrás de tu producción</p>
      </div>
    </div>
  );
}

function NavItem({ item, onNavigate, chatUnread, badge, onSeen }) {
  const Icon = item.icon;
  const handleClick = () => { if (badge > 0 && onSeen) onSeen(item.key); if (onNavigate) onNavigate(); };
  return (
    <NavLink key={item.key} to={item.path} end={item.path === "/"} onClick={handleClick} data-testid={`nav-${item.key}`}
      className={({ isActive }) =>
        `flex items-center gap-3 mx-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive ? "bg-primary/15 text-primary shadow-soft"
            : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5 hover:translate-x-0.5"}`}>
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1">{item.label}</span>
      {item.key === "chat" && chatUnread > 0 && (
        <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="nav-chat-unread">{chatUnread > 99 ? "99+" : chatUnread}</span>
      )}
      {item.key !== "chat" && badge > 0 && (
        <span className="ml-auto h-2.5 w-2.5 rounded-full bg-primary shrink-0 animate-pulse" title={`${badge} nuevo(s)`} data-testid={`nav-dot-${item.key}`} />
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { can } = useAuth();
  const items = NAV.filter((n) => !n.hidden && (Array.isArray(n.perm) ? n.perm.some(can) : can(n.perm)));
  const [chatUnread, setChatUnread] = useState(0);
  const [navBadges, setNavBadges] = useState({});
  const [mq, setMq] = useState("");
  useEffect(() => {
    let stop = false;
    const load = () => {
      api.get("/chat/unread").then((r) => { if (!stop) setChatUnread(r.data.unread || 0); }).catch(() => {});
      api.get("/nav-badges").then((r) => { if (!stop) setNavBadges(r.data || {}); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  const markSeen = (key) => { setNavBadges((b) => { if (!b[key]) return b; const c = { ...b }; delete c[key]; return c; }); api.post(`/nav-badges/${key}/seen`).catch(() => {}); };
  const matches = mq.trim() ? smartSearch(items, mq, ["label", "group"]) : null;
  return (
    <>
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-2 h-9 px-3 rounded-xl border border-white/10 bg-white/5 focus-within:border-primary/50 transition-colors">
          <Search className="h-4 w-4 shrink-0 text-sidebar-foreground/50" />
          <input
            value={mq}
            onChange={(e) => setMq(e.target.value)}
            placeholder="Buscar módulo…"
            data-testid="sidebar-module-search"
            className="flex-1 min-w-0 bg-transparent outline-none text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/40"
          />
          {mq && <button onClick={() => setMq("")} data-testid="sidebar-module-search-clear" className="text-sidebar-foreground/50 hover:text-sidebar-foreground"><X className="h-4 w-4" /></button>}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav">
        {matches ? (
          <div className="mb-5">
            <p className="px-5 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sidebar-foreground/40">
              {matches.length} resultado(s)
            </p>
            {matches.length === 0
              ? <p className="px-5 text-sm text-sidebar-foreground/50" data-testid="sidebar-module-empty">Ningún módulo coincide con “{mq}”.</p>
              : matches.map((item) => <NavItem key={item.key} item={item} onNavigate={onNavigate} chatUnread={chatUnread} badge={navBadges[item.key] || 0} onSeen={markSeen} />)}
          </div>
        ) : (
          GROUP_ORDER.map((group) => {
            const groupItems = items.filter((i) => i.group === group);
            if (groupItems.length === 0) return null;
            return (
              <div key={group} className="mb-5">
                <p className="px-5 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sidebar-foreground/40">{group}</p>
                {groupItems.map((item) => <NavItem key={item.key} item={item} onNavigate={onNavigate} chatUnread={chatUnread} badge={navBadges[item.key] || 0} onSeen={markSeen} />)}
              </div>
            );
          })
        )}
      </nav>
    </>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "ahora";
  const m = Math.floor(s / 60); if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `hace ${h} h`;
  const dd = Math.floor(h / 24); if (dd < 7) return `hace ${dd} d`;
  return d.toLocaleDateString("es-GT", { day: "2-digit", month: "short" });
}

function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(() => { if (!open) load(); }, 30000); return () => clearInterval(t); }, [open]);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const clearAll = async () => { await api.post("/notifications/clear"); load(); };
  const readOne = async (n) => {
    if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); }
    setData((d) => ({ items: d.items.map((x) => x.id === n.id ? { ...x, read: true } : x), unread: Math.max(0, d.unread - (n.read ? 0 : 1)) }));
    if (n.link) { setOpen(false); navigate(n.link); }
  };
  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) { load(); }
    else if (data.unread > 0) { await api.post("/notifications/read-all").catch(() => {}); load(); }
  };
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 hover:bg-muted rounded-xl transition-colors" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-count">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-1">
          <DropdownMenuLabel>Notificaciones{data.unread > 0 ? ` · ${data.unread} nuevas` : ""}</DropdownMenuLabel>
          <div className="flex items-center gap-3 pr-2">
            {data.unread > 0 && <button className="text-xs text-primary font-semibold" onClick={markAll} data-testid="notif-read-all">Marcar leídas</button>}
            {data.items.length > 0 && <button className="text-xs text-muted-foreground hover:text-destructive font-semibold" onClick={clearAll} data-testid="notif-clear-all">Borrar</button>}
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="px-3 py-6 text-sm text-muted-foreground text-center">Sin notificaciones.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => readOne(n)} data-testid="notif-item"
              className={`w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-l-2 ${n.read ? "border-transparent opacity-70" : "border-primary bg-primary/5"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" data-testid="notif-unread-dot" />}
                <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-tight ${n.read ? "font-medium" : "font-bold"}`}>{n.title}</p>
                    <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">{timeAgo(n.created_at)}</span>
                  </div>
                  {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children, title }) {
  const { user, logout, switchRole } = useAuth();
  const location = useLocation();
  const helpSlug = slugForRoute(location.pathname);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.must_change_password) setPwOpen(true); }, [user]);

  const handleLogout = () => { api.post("/auth/logout").catch(() => {}); logout(); navigate("/login"); };
  const initials = (() => {
    const parts = (user?.name || user?.username || "?").trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase() || "?";
  })();

  const changePw = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Las contraseñas no coinciden");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Contraseña actualizada");
      setPwOpen(false); setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <WelcomeTour />
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground fixed top-4 bottom-4 left-4 z-30 rounded-3xl shadow-float overflow-hidden">
        <Brand />
        <SidebarContent />
        <div className="p-3 border-t border-white/10 text-[10px] text-sidebar-foreground/40 uppercase tracking-widest">SELEC — Soluciones Eléctricas</div>
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-sidebar text-sidebar-foreground">
            <button className="absolute right-4 z-10 text-sidebar-foreground" style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }} onClick={() => setMobileOpen(false)} data-testid="close-drawer"><X className="h-5 w-5" /></button>
            <Brand />
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-[18.5rem] flex flex-col min-w-0">
        <header className="min-h-[4rem] border-b border-border/60 bg-card/80 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="open-drawer"><Menu className="h-6 w-6" /></button>
            <h1 className="font-heading font-bold text-xl sm:text-2xl truncate tracking-tight">{title}</h1>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <GtClock />
            {helpSlug && <ScreenHelp slug={helpSlug} compact />}
            <GlobalSearch />
            <Button variant="outline" size="icon" onClick={() => window.location.reload()} title="Actualizar todo el sistema" data-testid="global-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <PushToggle />
            <NotificationsBell />
            <Button variant="outline" size="icon" onClick={() => setTheme(theme === "voltage_dark" ? "industrial_core" : "voltage_dark")} title={theme === "voltage_dark" ? "Modo claro" : "Modo oscuro"} data-testid="dark-mode-toggle">
              {theme === "voltage_dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="theme-switcher">
                  <Palette className="h-4 w-4" /><span className="hidden xl:inline">Tema</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Elige un tema</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {THEMES.map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => setTheme(t.id)} data-testid={`theme-${t.id}`} className="flex items-center justify-between cursor-pointer">
                    <div><p className="text-sm font-medium">{t.name}</p><p className="text-[11px] text-muted-foreground">{t.hint}</p></div>
                    {theme === t.id && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 hover:bg-muted rounded-xl transition-colors" data-testid="user-menu">
                  <div className="h-8 w-8 bg-primary text-primary-foreground flex items-center justify-center rounded-xl font-bold text-xs">{initials}</div>
                  <div className="hidden sm:block text-left leading-tight">
                    <p className="text-sm font-semibold truncate max-w-[140px]">{user?.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{user?.role_name}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">@{user?.username}</DropdownMenuLabel>
                {(user?.assigned_roles?.length || 0) > 1 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Cambiar de perfil</DropdownMenuLabel>
                    {user.assigned_roles.map((r) => (
                      <DropdownMenuItem
                        key={r.id}
                        onClick={() => { if (r.id !== user.active_role_id) switchRole(r.id); }}
                        className="cursor-pointer"
                        data-testid={`switch-role-${r.id}`}
                      >
                        <UserCog className={`h-4 w-4 mr-2 ${r.id === user.active_role_id ? "text-primary" : "text-muted-foreground"}`} />
                        <span className={r.id === user.active_role_id ? "font-semibold" : ""}>{r.name}</span>
                        {r.id === user.active_role_id && <span className="ml-auto text-[10px] text-primary font-bold">Activo</span>}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} className="cursor-pointer" data-testid="open-change-password">
                  <KeyRound className="h-4 w-4 mr-2" /> Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 animate-fade-in">{children}</main>
      </div>

      <Dialog open={pwOpen} onOpenChange={(o) => { if (!user?.must_change_password) setPwOpen(o); }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cambiar contraseña</DialogTitle>
            <DialogDescription>
              {user?.must_change_password ? "Por seguridad, debes cambiar tu contraseña temporal." : "Actualiza tu contraseña de acceso."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Contraseña actual</Label>
              <Input type="password" className="rounded-sm" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Nueva contraseña</Label>
              <Input type="password" className="rounded-sm" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Confirmar contraseña</Label>
              <Input type="password" className="rounded-sm" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" />
            </div>
          </div>
          <DialogFooter>
            {!user?.must_change_password && <Button variant="outline" className="rounded-sm" onClick={() => setPwOpen(false)}>Cancelar</Button>}
            <Button className="rounded-sm font-bold" onClick={changePw} disabled={saving || !pw.current_password || !pw.new_password} data-testid="save-password-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
